import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { SiteAuditComponent } from '@client/pages/engineering/components';
import { HttpService } from '@core/services';
import {
  EquipmentHttpService,
  SiteAuditHttpService,
} from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EquipmentType } from '@models/enums';
import { ScopingActions } from '@store/actions';
import {
  DDVSelectors,
  ProjectSelectors,
  ScopingSelectors,
  SiteAuditSelectors,
  UserSelectors,
} from '@store/selectors';

import './styles.scss';

type SiteAuditTabProps = {
  isActive?: boolean;
};

export const SiteAuditTab: FC<SiteAuditTabProps> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    ScopingSelectors.getTabLoadingState('siteAudit'),
  );
  const isDataLoaded = useSelector(
    ScopingSelectors.getTabLoadedState('siteAudit'),
  );
  const permissions = useSelector(
    UserSelectors.getEngineeringSiteAuditPermissions,
  );
  const { projectType } = useSelector(
    SiteAuditSelectors.getProjectInfo('projectType'),
  );
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType as string),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringScoping,
      Tab.SiteAudit,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        ScopingActions.setTabLoading.done({
          tabName: 'siteAudit',
          isLoading: true,
        }),
      );

      const loadSiteAuditTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(
            EquipmentHttpService,
          ).getEquipmentTypes();
          await HttpService.getHttpRequests(
            EquipmentHttpService,
          ).getEquipmentsByTypes([EquipmentType.Cable]);
          await HttpService.getHttpRequests(
            EquipmentHttpService,
          ).getEquipmentsByManufacturers(['Raycap']);
          await HttpService.getHttpRequests(SiteAuditHttpService).getSiteAudit(
            projectBundleID,
          );
          dispatch(
            ScopingActions.setTabLoaded.done({
              tabName: 'siteAudit',
              isLoaded: true,
            }),
          );
        } catch {
          dispatch(
            ScopingActions.setTabError.done({
              tabName: 'siteAudit',
              hasError: true,
            }),
          );
        }
      };

      loadSiteAuditTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-site-audit-tab loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="prov-site-audit-tab">
      <SiteAuditComponent
        defaultEditing={false}
        permissions={permissions}
        canUserEdit={
          canEdit &&
          projectTypePermission &&
          canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
        }
      />
    </div>
  );
};
